import { z } from 'zod';

import {
  SharePlanType,
  SharePlanVestingType,
  TerminationInterval,
  VestingOnOption,
} from '../../../types/pool-plans.types';
import { getTranslation } from '../../../utils/get-translation.util';
import { ACCEPTED_FILE_MIME_TYPES, MAX_FILE_SIZE } from '../pool-form/pool-form.validation';

const hasInputAfterLeadingZero = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const trimmedValue = value.trim();
  return /^0\d+/.test(trimmedValue);
};

const requiredPriceValue = z.preprocess(
  (value) => {
    if (typeof value === 'string' && value.trim() === '') return Number.NaN;
    if (hasInputAfterLeadingZero(value)) return Number.NaN;
    return value;
  },
  z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .min(0, { message: getTranslation('REQUIRED') }),
);

const optionalPriceValue = z.preprocess(
  (value) => {
    if (value === null || (typeof value === 'string' && value.trim() === '')) return undefined;
    if (hasInputAfterLeadingZero(value)) return Number.NaN;
    return value;
  },
  z.union([
    z.undefined(),
    z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(0, { message: getTranslation('REQUIRED') }),
  ]),
);

const hurdle = z.object({
  enabled: z.boolean(),
  value: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .min(1, { message: getTranslation('REQUIRED') }),
});

const stockOptions = z.object({
  enabled: z.boolean(),
  value: requiredPriceValue,
  conversionRatio: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .optional(),
});
const warrants = z.object({
  enabled: z.boolean(),
  value: requiredPriceValue,
  conversionRatio: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .optional(),
});

const sar = z.object({
  enabled: z.boolean(),
  value: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .min(1, { message: getTranslation('REQUIRED') }),
});

const rsu = z.object({
  enabled: z.boolean(),
  conversionRatio: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .optional(),
});

const phantom = z.object({
  enabled: z.boolean(),
  conversionRatio: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .optional(),
});

const rsa = z.object({
  enabled: z.boolean(),
  value: optionalPriceValue,
  conversionRatio: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .optional(),
});

const commonStock = z.object({
  enabled: z.boolean().optional(),
});

const bspce = z.object({
  enabled: z.boolean(),
  value: requiredPriceValue,
  conversionRatio: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .optional(),
});

export const stepOne = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: getTranslation('REQUIRED') })
    .max(100, getTranslation('MAXIMUM_CHARACTERS', { count: 100 })),
  pool: z.object({
    name: z.string(),
    id: z.string(),
  }),
  date: z.coerce.date(),
  type: z.enum(SharePlanType),
  financialDetails: z.object({
    [SharePlanType.GROWTH_HURDLE]: hurdle.optional(),
    [SharePlanType.STOCK_OPTIONS]: stockOptions.optional(),
    [SharePlanType.WARRANTS]: warrants.optional(),
    [SharePlanType.SAR]: sar.optional(),
    [SharePlanType.RSA]: rsa.optional(),
    [SharePlanType.RSU]: rsu.optional(),
    [SharePlanType.PHANTOM]: phantom.optional(),
    [SharePlanType.COMMON_STOCK]: commonStock.optional(),
    [SharePlanType.BSPCE]: bspce.optional(),
  }),
});

export const formSchemaTimeBasedStandard = z
  .object({
    vestingOn: z.enum(VestingOnOption),
    vestingDuration: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(1, { message: getTranslation('REQUIRED') }),
    vestingFrequency: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(1, { message: getTranslation('REQUIRED') }),
    cliffPeriod: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .optional(),
    accelerationDate: z.coerce.date().optional(),
  })
  .refine(({ vestingDuration, vestingFrequency }) => vestingDuration % vestingFrequency === 0, {
    message: getTranslation('DURATION_SHOULD_BE_MULTIPLE_OF_FREQUENCY'),
    path: ['vestingDuration'],
  })
  .refine(({ vestingDuration, vestingFrequency }) => vestingFrequency <= vestingDuration, {
    message: getTranslation('FREQUENCY_CANNOT_EXCEED_DURATION'),
    path: ['vestingFrequency'],
  })
  .refine(
    ({ vestingDuration, cliffPeriod }) => {
      if (cliffPeriod === undefined || cliffPeriod === null) return true;
      return cliffPeriod <= vestingDuration;
    },
    {
      message: getTranslation('CLIFF_CANNOT_EXCEED_DURATION'),
      path: ['cliffPeriod'],
    },
  )
  .refine(
    ({ cliffPeriod, vestingFrequency }) => {
      if (cliffPeriod == null || vestingFrequency == null) return true;
      return vestingFrequency === 0 ? false : cliffPeriod % vestingFrequency === 0;
    },
    {
      message: getTranslation('CLIFF_SHOULD_BE_DIVISIBLE_BY_FREQUENCY'),
      path: ['cliffPeriod'],
    },
  );

export const formSchemaTimeBasedDynamic = z
  .object({
    vestingOn: z.enum(VestingOnOption, { error: getTranslation('MUST_SELECT_VESTING_OPTION') }),

    enabled: z.boolean(),
    dynamicVestings: z
      .array(
        z
          .object({
            vestingDuration: z.coerce.number().min(1, { message: getTranslation('REQUIRED') }),
            vestingFrequency: z.coerce.number().min(1, { message: getTranslation('REQUIRED') }),
            planAllocation: z.coerce.number().min(1, { message: getTranslation('REQUIRED') }),
          })
          .refine(
            ({ vestingDuration, vestingFrequency }) => vestingDuration % vestingFrequency === 0,
            {
              message: getTranslation('DURATION_SHOULD_BE_MULTIPLE_OF_FREQUENCY'),
              path: ['dynamicVestings'],
            },
          ),
      )
      .max(10, {
        message: getTranslation('CANNOT_ADD_MORE_THAN_COUNT_ITEMS', { count: 10 }),
      }),
    accelerationDate: z.coerce.date().optional(),
  })
  .refine(
    ({ dynamicVestings, enabled }) =>
      enabled
        ? dynamicVestings.reduce((sum, vesting) => sum + (vesting.planAllocation ?? 0), 0) === 100
        : true,
    {
      message: getTranslation('PLAN_ALLOCATION_DOES_NOT_REACH_100'),
      path: ['dynamicVestings'],
    },
  );

export const formSchemaTargetBased = z
  .object({
    enabled: z.boolean(),
    targetVestings: z
      .array(
        z
          .object({
            name: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            weight: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .max(100),
            targetDate: z.coerce.date(),
          })
          .refine(
            ({ name, weight, targetDate }) => {
              if (!name || weight === undefined) return true;
              return targetDate.getTime() !== new Date(+0).getTime();
            },
            {
              message: getTranslation('INVALID_TARGET_DATE'),
              path: ['targetDate'],
            },
          ),
      )
      .max(10, {
        message: getTranslation('CANNOT_ADD_MORE_THAN_COUNT_ITEMS', { count: 10 }),
      }),
  })
  .refine(
    ({ targetVestings, enabled }) =>
      enabled
        ? targetVestings.reduce((sum, vesting) => sum + (vesting.weight ?? 0), 0) === 100
        : true,
    {
      message: getTranslation('WEIGHT_ALLOCATION_DOES_NOT_REACH_100'),
      path: ['targetVestings'],
    },
  );

const vesting = z
  .object({
    enabled: z.boolean(),
    vestingMechanism: z.enum(SharePlanVestingType),
    timeBasedStandard: formSchemaTimeBasedStandard.optional(),
    timeBasedDynamic: formSchemaTimeBasedDynamic.optional(),
    targetBasedStandard: formSchemaTargetBased.optional(),
  })
  .refine(
    ({ targetBasedStandard, timeBasedDynamic, timeBasedStandard }) =>
      targetBasedStandard || timeBasedDynamic || timeBasedStandard,
  );

export const stepTwo = z
  .object({
    vesting: vesting.optional(),
    expiryDate: z
      .object({
        enabled: z.boolean(),
        date: z.coerce.date(),
      })
      .optional(),
  })
  .optional();

const terminationCauseSchema = z.union([
  z.object({
    enabled: z.literal(true),
    value: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }).min(1),
    interval: z.enum(TerminationInterval),
  }),
  z.object({
    enabled: z.literal(false),
  }),
]);

export const formSchemaTerminationLogic = z.object({
  enabled: z.boolean(),
  resignation: terminationCauseSchema,
  termination: terminationCauseSchema,
  terminationWithCause: terminationCauseSchema,
  retirement: terminationCauseSchema,
  death: terminationCauseSchema,
  disability: terminationCauseSchema,
});

export const stepThree = z
  .object({
    terminationLogic: formSchemaTerminationLogic.optional(),
  })
  .optional();

export const stepFour = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        id: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, getTranslation('FILE_SIZE_TOO_LARGE')),
      })
      .array()
      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne,
  stepTwo,
  stepThree,
  stepFour,
});

export type FormSchema = z.infer<typeof formSchema>;
